import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaCalendarAlt, FaCheck } from 'react-icons/fa';
import { timeSlots } from '../data/sampleData';
import { reservasService, negociosService, personalService, servicioService, diasCanceladosService, pagosService } from '../../services/api';
import { barberiaCache } from '../data/barberiaCache';
import { useAuth } from '../../context/AuthContext';
import { useEstablecimiento } from '../../context/EstablecimientoContext';
import { scrollToTop } from '../../hooks/useScrollToTop';
import TransferPaymentModal from '../../components/shared/TransferPaymentModal';
import ReservaActivaModal from '../../components/shared/ReservaActivaModal';
import { fechaCalendarioDesdeApi, formatearFechaYYYYMMDD, hoyYYYYMMDDEnNegocio } from '../../utils/fecha';
import './Booking.css';

const Booking = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { codigo: establecimiento, basePath } = useEstablecimiento();

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    });
  };

  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedBarber, setSelectedBarber] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({
    notes: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState(timeSlots);
  const [isLoadingTimes, setIsLoadingTimes] = useState(false);
  const [error, setError] = useState('');
  const [team, setTeam] = useState([]); // Estado para el equipo cargado desde el backend
  const [services, setServices] = useState([]); // Estado para los servicios cargados desde el backend

  const [establecimientoConfig, setEstablecimientoConfig] = useState(null);
  const [diasDisponibles, setDiasDisponibles] = useState([1, 2, 3, 4, 5, 6]); // Por defecto: Lunes a Sábado
  const [horaCierre, setHoraCierre] = useState('20:00');
  const [diasCancelados, setDiasCancelados] = useState([]); // Lista de días cancelados
  const [isLoadingDias, setIsLoadingDias] = useState(true); // true hasta que se cargue la config (días abiertos + cancelados)
  const [isPaying, setIsPaying] = useState(false);
  const [pagoError, setPagoError] = useState('');
  const [createdReservaId, setCreatedReservaId] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [pagoConfirmado, setPagoConfirmado] = useState(false);
  const [tieneReservaActiva, setTieneReservaActiva] = useState(false);
  const [reservaActivaInfo, setReservaActivaInfo] = useState(null);
  const [showReservaActivaModal, setShowReservaActivaModal] = useState(false);
  const [avisoReservaActivaMostrado, setAvisoReservaActivaMostrado] = useState(false);

  // Al confirmar o cambiar de paso, subir al inicio
  useEffect(() => {
    scrollToTop();
  }, [step, isSubmitted]);

  // Función para convertir datos del backend al formato esperado
  const convertirPersonalABackend = (personalBackend) => {
    return personalBackend.map(p => ({
      id: p.idPersonal,
      name: p.nombre,
      role: p.rol,
      avatar: p.avatar || '/assets/img/establecimientos/barberia_ejemplo/personal/personal1.jpg',
      specialties: p.specialties || []
    }));
  };

  // Función para convertir servicios del backend al formato esperado
  const convertirServicioABackend = (serviciosBackend) => {
    return serviciosBackend.map(s => ({
      id: s.idServicio,
      name: s.nombre,
      category: s.categoria,
      duration: s.duracion,
      price: s.precio,
      description: s.descripcion
    }));
  };

  // Verificar si el usuario ya tiene un turno activo
  useEffect(() => {
    const verificarReservaActiva = async () => {
      if (!user?.email) {
        setTieneReservaActiva(false);
        setReservaActivaInfo(null);
        return;
      }
      try {
        const res = await reservasService.tieneReservaActiva(user.email);
        const data = res?.data ?? res;
        const activa = !!data?.tieneActiva;
        setTieneReservaActiva(activa);
        setReservaActivaInfo(activa ? data : null);
      } catch (err) {
        console.error('Error al verificar reserva activa:', err);
        setTieneReservaActiva(false);
        setReservaActivaInfo(null);
      }
    };
    verificarReservaActiva();
  }, [user?.email]);

  const avisarSiTieneReservaActiva = () => {
    if (tieneReservaActiva && !avisoReservaActivaMostrado) {
      setShowReservaActivaModal(true);
      setAvisoReservaActivaMostrado(true);
    }
  };

  // Si entró con ?service= o ya avanzó de paso, avisar cuando se detecta turno activo
  useEffect(() => {
    if (tieneReservaActiva && step >= 2) {
      avisarSiTieneReservaActiva();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tieneReservaActiva, step]);

  // Cargar personal y servicios en paralelo; usar caché de barbería si existe para mostrar al instante
  useEffect(() => {
    const applyServicioFromUrl = (serviciosConvertidos) => {
      const searchParams = new URLSearchParams(location.search);
      const serviceIdFromUrl = searchParams.get('service');
      if (serviceIdFromUrl && serviciosConvertidos.length > 0) {
        const servicioEncontrado = serviciosConvertidos.find(
          s => s.id.toString() === serviceIdFromUrl
        );
        if (servicioEncontrado) {
          setSelectedService(servicioEncontrado);
          setStep(2);
        }
      }
    };

    const cachedPersonal = barberiaCache.getPersonal(establecimiento);
    const cachedServicios = barberiaCache.getServicios(establecimiento);
    if (cachedPersonal && Array.isArray(cachedPersonal)) {
      setTeam(convertirPersonalABackend(cachedPersonal));
    }
    if (cachedServicios && Array.isArray(cachedServicios)) {
      const serviciosConvertidos = convertirServicioABackend(cachedServicios);
      setServices(serviciosConvertidos);
      applyServicioFromUrl(serviciosConvertidos);
    }

    const cargarTodo = async () => {
      try {
        const [personalRes, serviciosRes] = await Promise.all([
          personalService.obtenerPersonal(establecimiento),
          servicioService.obtenerServicios(establecimiento)
        ]);
        const personalData = personalRes?.data ?? personalRes;
        const serviciosData = serviciosRes?.data ?? serviciosRes;
        const personalConvertido = convertirPersonalABackend(Array.isArray(personalData) ? personalData : []);
        const serviciosConvertidos = convertirServicioABackend(Array.isArray(serviciosData) ? serviciosData : []);
        setTeam(personalConvertido);
        setServices(serviciosConvertidos);
        if (Array.isArray(personalData)) barberiaCache.setPersonal(establecimiento, personalData);
        if (Array.isArray(serviciosData)) barberiaCache.setServicios(establecimiento, serviciosData);
        applyServicioFromUrl(serviciosConvertidos);
      } catch (err) {
        console.error('Error al cargar personal/servicios:', err);
        if (!cachedPersonal) setTeam([]);
        if (!cachedServicios) setServices([]);
      }
    };
    cargarTodo();
  }, [establecimiento, location.search]);

  // Cargar configuración del establecimiento: usar caché al instante si existe (precargada desde Servicios), luego refrescar en segundo plano
  useEffect(() => {
    const cachedNegocio = barberiaCache.getNegocio(establecimiento);
    const cachedDiasCancelados = barberiaCache.getDiasCancelados(establecimiento);
    if (cachedNegocio || cachedDiasCancelados) {
      if (cachedNegocio) {
        setEstablecimientoConfig(cachedNegocio);
        if (Array.isArray(cachedNegocio.diasDisponibles)) {
          setDiasDisponibles([...cachedNegocio.diasDisponibles].sort((a, b) => a - b));
        }
        if (cachedNegocio.horarios?.fin) setHoraCierre(cachedNegocio.horarios.fin);
      }
      if (Array.isArray(cachedDiasCancelados)) setDiasCancelados(cachedDiasCancelados);
      setIsLoadingDias(false);
    } else {
      setIsLoadingDias(true);
    }

    const fetchEstablecimientoConfig = async () => {
      try {
        const hoy = new Date();
        const fechaDesde = hoyYYYYMMDDEnNegocio();
        const [negocioResponse, diasCanceladosResponse] = await Promise.all([
          negociosService.obtenerNegocio(establecimiento),
          diasCanceladosService.obtenerDiasCancelados(establecimiento, fechaDesde).catch(() => ({ data: [] }))
        ]);
        const negocio = negocioResponse?.data ?? negocioResponse;
        const diasCanceladosData = diasCanceladosResponse?.data ?? diasCanceladosResponse ?? [];
        setEstablecimientoConfig(negocio || null);
        setDiasCancelados(Array.isArray(diasCanceladosData) ? diasCanceladosData : []);
        if (negocio) {
          if (Array.isArray(negocio.diasDisponibles)) {
            setDiasDisponibles([...negocio.diasDisponibles].sort((a, b) => a - b));
          }
          if (negocio.horarios?.fin) setHoraCierre(negocio.horarios.fin);
          barberiaCache.setNegocio(establecimiento, negocio);
        }
        if (Array.isArray(diasCanceladosData)) barberiaCache.setDiasCancelados(establecimiento, diasCanceladosData);
      } catch (err) {
        console.error('Error al cargar configuración del establecimiento:', err);
      } finally {
        setIsLoadingDias(false);
      }
    };
    fetchEstablecimientoConfig();
  }, [establecimiento]);

  // Generar fechas disponibles (próximos 30 días) según configuración del establecimiento
  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();
    // Normalizar hoy a medianoche local para evitar problemas de zona horaria
    today.setHours(0, 0, 0, 0);
    
    // Crear un Set de fechas canceladas para búsqueda rápida
    const fechasCanceladasSet = new Set(
      diasCancelados.map(dia => formatearFechaYYYYMMDD(fechaCalendarioDesdeApi(dia.fecha))).filter(Boolean)
    );
    
    // Verificar si el día actual ya pasó la hora de cierre
    const ahora = new Date();
    const horaActual = ahora.getHours();
    const minutosActuales = ahora.getMinutes();
    const horaActualMinutos = horaActual * 60 + minutosActuales;
    
    // Obtener la hora de cierre según el día de la semana
    const diaSemanaHoy = today.getDay(); // 0 = Domingo, 1 = Lunes, ..., 6 = Sábado
    let horaCierreHoy = horaCierre;
    if (diaSemanaHoy === 6) { // Sábado
      horaCierreHoy = '18:00';
    } else if (diaSemanaHoy >= 1 && diaSemanaHoy <= 5) { // Lunes a Viernes
      horaCierreHoy = '20:00';
    }
    
    const [horasCierre, minutosCierre] = horaCierreHoy.split(':').map(Number);
    const horaCierreMinutos = horasCierre * 60 + minutosCierre;
    const estaCerradoHoy = horaActualMinutos >= horaCierreMinutos;
    
    // Empezar desde hoy (i = 0) hasta 30 días en el futuro
    // Si el local está cerrado hoy, empezar desde mañana (i = 1)
    const inicio = estaCerradoHoy ? 1 : 0;
    
    for (let i = inicio; i <= 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dayOfWeek = date.getDay(); // 0 = Domingo, 1 = Lunes, ..., 6 = Sábado
      
      // Formatear fecha como YYYY-MM-DD sin problemas de zona horaria
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const fechaStr = `${year}-${month}-${day}`;
      
      // Incluir solo los días disponibles según configuración y que no estén cancelados
      if (diasDisponibles.includes(dayOfWeek) && !fechasCanceladasSet.has(fechaStr)) {
        dates.push({
          value: fechaStr,
          label: date.toLocaleDateString('es-ES', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })
        });
      }
    }
    
    return dates;
  };

  // Actualizar fechas cuando cambia la configuración o la hora de cierre
  const availableDates = generateAvailableDates();
  
  useEffect(() => {
    // Las fechas se regeneran automáticamente cuando cambia diasDisponibles o horaCierre
  }, [diasDisponibles, horaCierre]);

  const handleServiceSelect = (service) => {
    avisarSiTieneReservaActiva();
    setSelectedService(service);
    setStep(2);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setStep(3); // Cambiar a paso 3 (Profesional)
    // Resetear horarios cuando cambia la fecha
    setAvailableTimeSlots(timeSlots);
  };

  // Cargar horarios cuando estamos en paso 4 (Hora) y ya hay profesional seleccionado
  useEffect(() => {
    const fetchAvailableTimes = async () => {
      if (step === 4 && selectedDate && selectedBarber && selectedService) {
        setIsLoadingTimes(true);
        setError('');
        try {
          const response = await reservasService.obtenerHorariosDisponibles(
            establecimiento,
            selectedDate,
            selectedBarber.id,
            selectedService
          );
          const horarios = response.data?.horariosDisponibles || response.horariosDisponibles || [];
          setAvailableTimeSlots(horarios);
        } catch (err) {
          setError('Error al cargar horarios disponibles');
          setAvailableTimeSlots(timeSlots);
        } finally {
          setIsLoadingTimes(false);
        }
      }
    };

    fetchAvailableTimes();
  }, [step, selectedDate, selectedBarber, selectedService, establecimiento]);

  const handleBarberSelect = (barber) => {
    setSelectedBarber(barber);
    setIsLoadingTimes(!!(selectedDate && selectedService));
    setStep(4);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setStep(5); // Cambiar a paso 5 (Datos)
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (tieneReservaActiva) {
      setError('No podés confirmar: ya tenés un turno activo.');
      setShowReservaActivaModal(true);
      return;
    }
    
    if (!user || !user.email) {
      setError('Debes iniciar sesión para realizar una reserva');
      return;
    }

    try {
      const reservaData = {
        establecimiento,
        fecha: selectedDate,
        hora: selectedTime,
        servicio: {
          id: selectedService.id,
          name: selectedService.name,
          duration: selectedService.duration,
          price: selectedService.price
        },
        profesional: {
          id: selectedBarber.id,
          name: selectedBarber.name
        },
        notas: customerInfo.notes,
        usuarioEmail: user.email
      };

      const res = await reservasService.crearReserva(reservaData);
      const creada = res.data ?? res;
      setCreatedReservaId(creada?.id || null);
      setIsSubmitted(true);
    } catch (err) {
      console.error('Error al crear reserva:', err);
      setError(err.message || 'Error al crear la reserva. Por favor intenta nuevamente.');
    }
  };

  const volverAInicio = () => {
    navigate(basePath);
    scrollToTop();
  };

  const metodoPago = establecimientoConfig?.metodoPago || 'NINGUNO';
  const pagoHabilitado = !!establecimientoConfig?.pagoHabilitado;
  const esTransferencia = metodoPago === 'TRANSFERENCIA' && pagoHabilitado && !!establecimientoConfig?.pagoTransferencia;
  const esMercadoPago = metodoPago === 'MERCADO_PAGO' && !!establecimientoConfig?.mercadoPagoHabilitado;

  const handlePagar = async () => {
    setPagoError('');
    if (esTransferencia) {
      if (!createdReservaId) {
        setPagoError('No se encontró la reserva para confirmar el pago');
        return;
      }
      setShowTransferModal(true);
      return;
    }
    if (!esMercadoPago || !selectedService?.id) return;
    setIsPaying(true);
    try {
      const response = await pagosService.crearPreferencia({
        establecimiento,
        servicioId: selectedService.id,
        reservaId: createdReservaId || undefined,
        payerEmail: user?.email
      });
      const data = response.data ?? response;
      const initPoint = data.initPoint;
      if (!initPoint) {
        throw new Error('No se recibió la URL de Mercado Pago');
      }
      window.location.href = initPoint;
    } catch (err) {
      console.error('Error al iniciar pago Mercado Pago:', err);
      setPagoError(err.message || 'No se pudo iniciar el pago. Intentá de nuevo.');
      setIsPaying(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="booking-success">
        <div className="success-content">
          <div className="success-icon">
            <FaCheck />
          </div>
          <h2>¡Reserva Confirmada!</h2>
          <p>Tu turno ha sido reservado exitosamente.</p>
          <div className="booking-details">
            <div className="detail-item">
              <strong>Servicio:</strong> {selectedService.name}
            </div>
            <div className="detail-item">
              <strong>Fecha:</strong> {formatDate(selectedDate)}
            </div>
            <div className="detail-item">
              <strong>Hora:</strong> {selectedTime}
            </div>
            <div className="detail-item">
              <strong>Profesional:</strong> {selectedBarber.name}
            </div>
            {selectedService.price && (
              <div className="detail-item">
                <strong>Monto:</strong> {selectedService.price}
              </div>
            )}
            <div className="detail-item">
              <strong>Nota:</strong>{' '}
              {customerInfo.notes?.trim() ? (
                customerInfo.notes.trim()
              ) : (
                <em>Vacía</em>
              )}
            </div>
          </div>
          {pagoError && <div className="booking-pago-error">{pagoError}</div>}
          {pagoConfirmado && (
            <div className="booking-pago-ok">Pago confirmado. Gracias.</div>
          )}
          <div className="success-actions">
            <button type="button" onClick={volverAInicio} className="btn btn-primary">
              Volver a inicio
            </button>
            {(esTransferencia || esMercadoPago) && !pagoConfirmado && (
              <button
                type="button"
                className="btn btn-mercadopago"
                onClick={handlePagar}
                disabled={isPaying}
              >
                <img
                  src="/assets/img/logos_genericos/mercadoPago.png"
                  alt=""
                  className="btn-mercadopago-logo"
                />
                <span>
                  {isPaying ? (esMercadoPago ? 'Redirigiendo...' : 'Cargando...') : 'Pagar'}
                </span>
              </button>
            )}
          </div>
        </div>
        <TransferPaymentModal
          isOpen={showTransferModal}
          onClose={() => setShowTransferModal(false)}
          establecimiento={establecimiento}
          reservaId={createdReservaId}
          pagoTransferencia={establecimientoConfig?.pagoTransferencia}
          onSuccess={() => setPagoConfirmado(true)}
        />
        <ReservaActivaModal
          isOpen={showReservaActivaModal}
          onClose={() => setShowReservaActivaModal(false)}
          reservaActiva={reservaActivaInfo}
        />
      </div>
    );
  }

  return (
    <section className="booking" id="reservar">
      <div className="container">
        {/* Indicador de pasos */}
        <div className="booking-steps">
          {[1, 2, 3, 4, 5].map((stepNumber) => (
            <div 
              key={stepNumber} 
              className={`step ${step >= stepNumber ? 'active' : ''} ${step > stepNumber ? 'completed' : ''}`}
            >
              <div className="step-number">{stepNumber}</div>
              <div className="step-label">
                {stepNumber === 1 && 'Servicio'}
                {stepNumber === 2 && 'Fecha'}
                {stepNumber === 3 && 'Profesional'}
                {stepNumber === 4 && 'Hora'}
                {stepNumber === 5 && 'Datos'}
              </div>
            </div>
          ))}
        </div>

        <div className="booking-content">
          {/* Paso 1: Selección de servicio */}
          {step === 1 && (
            <div className="booking-step">
              <h3>Selecciona un Servicio</h3>
              {services.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p>Cargando servicios...</p>
                </div>
              ) : (
                <div className="services-grid">
                  {services.map(service => (
                  <div 
                    key={service.id} 
                    className="service-option"
                    onClick={() => handleServiceSelect(service)}
                  >
                    <h4>{service.name}</h4>
                    <div className="service-meta">
                      <span className="duration">
                        <img 
                          src="/assets/img/logos_genericos/reloj.png" 
                          alt="Duración" 
                          className="duration-icon"
                        />
                        {service.duration}
                      </span>
                      <span className="price">
                        <img 
                          src="/assets/img/logos_genericos/dinero.png" 
                          alt="Precio" 
                          className="price-icon"
                        />
                        {service.price}
                      </span>
                    </div>
                    <p>{service.description}</p>
                  </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Paso 2: Selección de fecha */}
          {step === 2 && (
            <div className="booking-step">
              <h3>Selecciona una Fecha</h3>
              {isLoadingDias ? (
                <div className="booking-loading-dias">
                  <p>Cargando días...</p>
                  <p className="booking-loading-dias-sub">Se muestran solo los días en que el local está abierto</p>
                </div>
              ) : (
                <>
                  <div className="dates-grid">
                    {availableDates.map(date => (
                      <div 
                        key={date.value} 
                        className="date-option"
                        onClick={() => handleDateSelect(date.value)}
                      >
                        <FaCalendarAlt className="date-icon" />
                        <span>{date.label}</span>
                      </div>
                    ))}
                  </div>
                  {availableDates.length === 0 && (
                    <p className="booking-sin-fechas">No hay fechas disponibles para reservar en los próximos días.</p>
                  )}
                </>
              )}
              <button 
                onClick={() => setStep(1)} 
                className="btn btn-secondary"
              >
                Volver
              </button>
            </div>
          )}

          {/* Paso 3: Selección de barbero */}
          {step === 3 && (
            <div className="booking-step">
              <h3>Selecciona un Profesional</h3>
              {team.length === 0 ? (
                <div className="loading-message">Cargando profesionales...</div>
              ) : (
                <div className="barbers-grid">
                  {team.map(barber => (
                  <div 
                    key={barber.id} 
                    className="barber-option"
                    onClick={() => handleBarberSelect(barber)}
                  >
                    <img src={barber.avatar} alt={barber.name} className="barber-avatar" />
                    <h4>{barber.name}</h4>
                    <p>{barber.role}</p>
                    {barber.specialties && barber.specialties.length > 0 && (
                      <div className="barber-specialties">
                        {barber.specialties.map((specialty, index) => (
                          <span key={index} className="specialty-tag">
                            {specialty}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  ))}
                </div>
              )}
              <button 
                onClick={() => setStep(2)} 
                className="btn btn-secondary"
              >
                Volver
              </button>
            </div>
          )}

          {/* Paso 4: Selección de hora */}
          {step === 4 && (
            <div className="booking-step">
              <h3>Selecciona una Hora</h3>
              {error && (
                <div className="error-message" style={{ color: 'red', marginBottom: '1rem', textAlign: 'center' }}>
                  {error}
                </div>
              )}
              {isLoadingTimes ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p>Cargando horarios disponibles...</p>
                </div>
              ) : !availableTimeSlots || availableTimeSlots.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p>No hay horarios disponibles para esta fecha y profesional.</p>
                  <button 
                    onClick={() => setStep(2)} 
                    className="btn btn-secondary"
                    style={{ marginTop: '1rem' }}
                  >
                    Seleccionar otra fecha
                  </button>
                </div>
              ) : (
                <>
                  <div className="times-grid">
                    {availableTimeSlots.map(time => (
                      <div 
                        key={time} 
                        className="time-option"
                        onClick={() => handleTimeSelect(time)}
                      >
                        <img 
                          src="/assets/img/logos_genericos/reloj.png" 
                          alt="Hora" 
                          className="time-icon"
                        />
                        <span>{time}</span>
                      </div>
                    ))}
                  </div>
                  <button 
                    onClick={() => setStep(3)} 
                    className="btn btn-secondary"
                  >
                    Volver
                  </button>
                </>
              )}
            </div>
          )}


          {/* Paso 5: Datos del cliente */}
          {step === 5 && (
            <div className="booking-step">
              <h3>Notas Adicionales (Opcional)</h3>
              {error && (
                <div className="error-message" style={{ color: 'red', marginBottom: '1rem', textAlign: 'center' }}>
                  {error}
                </div>
              )}
              <form onSubmit={handleSubmit} className="booking-form">
                <div className="form-group form-group-textarea">
                  <textarea
                    id="notes"
                    name="notes"
                    value={customerInfo.notes}
                    onChange={handleInputChange}
                    className="form-textarea"
                    placeholder="Alguna preferencia especial o comentario..."
                    maxLength={250}
                  />
                  <div className="character-count">
                    {(customerInfo.notes || '').length}/250
                  </div>
                </div>

                {tieneReservaActiva && (
                  <div className="booking-reserva-activa-bloqueada">
                    No podés confirmar esta reserva porque ya tenés un turno activo.
                    Cuando ese turno haya pasado, vas a poder reservar de nuevo.
                  </div>
                )}

                <div className="form-actions">
                  <button 
                    type="button"
                    onClick={() => setStep(4)} 
                    className="btn btn-secondary"
                  >
                    Volver
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={tieneReservaActiva}
                    title={tieneReservaActiva ? 'Tenés un turno activo' : undefined}
                  >
                    Confirmar Reserva
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
      <ReservaActivaModal
        isOpen={showReservaActivaModal}
        onClose={() => setShowReservaActivaModal(false)}
        reservaActiva={reservaActivaInfo}
      />
    </section>
  );
};

export default Booking;


